import React from 'react';
import { Stack, Divider, Group, ActionIcon, Box, Center, rem } from '@mantine/core';
import { ThemedText } from '../Typography';
import { useIsMobile } from '../../src/context/MediaQueryContext';
import { Search } from 'tabler-icons-react';
import { spotlight } from '@mantine/spotlight';
import { SearchResponse, getOrderedGroups, groupResultsByType } from './utils/searchHelpers';
import SearchResultItem from './SearchResultItem';
import { ThemedModal, ThemedLoader } from '../ui';
import GradientBadge from '../ui/Badges/GradientBadge';

interface SearchModalProps {
  opened: boolean;
  onClose: () => void;
  searchQuery: string;
  fullSearchResults: SearchResponse | null;
  isLoading: boolean;
  onBackToSearch: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({
  opened,
  onClose,
  searchQuery,
  fullSearchResults,
  isLoading,
  onBackToSearch,
}) => {
  const isMobile = useIsMobile();

  // Convert plural group names to singular
  const getSingularGroupName = (pluralName: string): string => {
    const singularMap: Record<string, string> = {
      'Dance Styles': 'Dance Style',
      Cities: 'City',
      Venues: 'Venue',
      Artists: 'Artist',
      Dictionary: 'Dictionary',
      'Event Types': 'Event Type',
      Organizers: 'Organizer',
      Posts: 'Post',
      Events: 'Event',
      Other: 'Other',
    };
    return singularMap[pluralName] || pluralName;
  };

  const handleBackToSearch = () => {
    onBackToSearch();
    setTimeout(() => {
      spotlight.open();
    }, 100); // Small delay to ensure modal closes first
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Center py={32}>
          <ThemedLoader />
        </Center>
      );
    }

    if (!fullSearchResults) {
      return null;
    }

    if (fullSearchResults.total_count === 0) {
      return (
        <Box p={isMobile ? 8 : 24}>
          <ThemedText variant="dimmed" size="sm" ta="center">
            No results found for "{fullSearchResults.query}"
          </ThemedText>
        </Box>
      );
    }

    const groupedResults = groupResultsByType(fullSearchResults.results);
    const orderedGroups = getOrderedGroups(groupedResults);

    return (
      <Box p={isMobile ? 16 : 24}>
        <ThemedText
          size="sm"
          mb={16}
          variant="dimmed"
          style={{
            fontSize: rem(12),
            opacity: 0.8,
          }}
        >
          Found {fullSearchResults.total_count} results
        </ThemedText>

        {orderedGroups.map((group) => {
          const isSingular = group.results.length === 1;
          const displayName = isSingular ? getSingularGroupName(group.name) : group.name;

          return (
            <Box key={group.name} mb={24}>
              <Group gap="xs" align="center" mb={8}>
                <ThemedText
                  size="md"
                  fw={600}
                  style={{
                    fontSize: rem(14),
                    lineHeight: rem(24),
                  }}
                >
                  {displayName}
                </ThemedText>
                <GradientBadge size="xs">{group.results.length}</GradientBadge>
              </Group>
              <Stack gap="xs">
                {group.results.map((result) => (
                  <SearchResultItem
                    key={result.id}
                    result={result}
                    isMobile={isMobile}
                    onClick={() => (window.location.href = result.url)}
                  />
                ))}
              </Stack>
              <Divider my="md" color="#374151" />
            </Box>
          );
        })}
      </Box>
    );
  };

  const modalTitle = (
    <Group justify="space-between" gap="4px" style={{ width: '100%' }}>
      <ActionIcon
        variant="filled"
        color="#EA580C"
        size="md"
        onClick={handleBackToSearch}
        title="Back to Search"
        style={{ marginRight: rem(8) }}
      >
        <Search style={{ width: rem(16), height: rem(24) }} />
      </ActionIcon>
      <ThemedText size="sm" fw={600}>
        Search Results for "{fullSearchResults?.query || searchQuery}"
      </ThemedText>
    </Group>
  );

  return (
    <ThemedModal
      opened={opened}
      onClose={onClose}
      size="md"
      title={modalTitle}
      navbarOffset={true}
      styles={{
        content: {
          backgroundColor: '#171717',
        },
        header: {
          backgroundColor: '#171717',
          borderBottom: '1px solid #374151',
        },
        body: {
          backgroundColor: '#171717',
          padding: 0,
        },
        close: {
          '&:hover': {
            backgroundColor: '#374151',
          },
        },
      }}
    >
      {/* ThemedModal scrolls its own content (#1875); a second scroll box
          here would nest scrollbars on short viewports. */}
      {renderContent()}
    </ThemedModal>
  );
};

export default SearchModal;
